import React from "react";
import { View, Text, Image, StyleSheet, Pressable } from "react-native";
import BaseMarketingController from "./BaseMarketingController";
import NtMarketingPageContainer from "../components/general/NtMarketingPageContainer";
import { useTheme } from "../../context/ThemeContext";
import { useMobile } from "../../hooks/useMobile";
import { useNavigation } from "@react-navigation/native";
import routes from "../../navigation/routes";
import {
  Reveal,
  WebClass,
  FONT_DISPLAY,
  FONT_MONO,
} from "../components/voxcraft/_shared";

// =============================================================================
// Impact Studies index — lists all 11 case studies as a grid. Reachable from
// the home page's "View all 11 impact studies →" button and from the footer's
// Impact Studies column links. Each card routes to the existing per-study
// controller via routes.impactstudy with the matching `type` param.
// =============================================================================

const STUDIES = [
  {
    type: "ncl", badge: "CRUISE", badgeKind: "cruise",
    logo: require("../../assets/images/partners/logo_ncl.png"), logoTileDark: true,
    metric: "+26%", metricLabel: "comm-package sales",
    name: "Norwegian Cruise Line",
    desc: "17-ship fleet deployed in one year, ahead of schedule, under budget.",
  },
  {
    type: "cunard", badge: "CRUISE", badgeKind: "cruise",
    logo: require("../../assets/images/partners/logo_cunard.png"), logoTileDark: true,
    metric: "113K tons", metricLabel: "Queen Anne launch",
    name: "Cunard · Queen Anne",
    desc: "Rugged Android devices for ~3,000 guests, integrated with Martec SMCS.",
  },
  {
    type: "disney", badge: "CRUISE", badgeKind: "cruise",
    logo: require("../../assets/images/partners/logo_disney.png"), logoTileDark: true,
    metric: "6 ft / 15 min", metricLabel: "CDC contact rule",
    name: "Disney Cruise Line",
    desc: "Wearables + BLE gateways for contact tracing; later heat-mapping.",
  },
  {
    type: "holand", badge: "HOSPITALITY", badgeKind: "hosp",
    logo: require("../../assets/images/partners/logo-holland-america-group-new.png"), logoTileDark: false,
    metric: "4,000+", metricLabel: "extensions, 9 hotels",
    name: "Princess Lodges & Westmark Hotels",
    desc: "Unified platform across nine Alaska hotels and 20+ office locations.",
  },
  {
    type: "four-seasons-yachts", badge: "ULTRA-LUXURY", badgeKind: "hosp",
    logo: require("../../assets/images/partners/logo_four-seasons-yachts.png"), logoTileDark: true,
    metric: "1:1", metricLabel: "staff-to-guest ratio",
    name: "Four Seasons Yachts · Four Seasons I",
    desc: "Discreet, offline-capable crew comms across the 95-suite, 207m superyacht.",
  },
  {
    type: "tritan", badge: "HEALTHCARE", badgeKind: "hosp",
    logo: null,
    wmLine1: "TRITAN",
    wmLine2: "SeaCare",
    metric: "Realtime", metricLabel: "vital screening",
    name: "Tritan Software · SeaCare",
    desc: "Noninvasive vital screening, chatbot triage, virtual consults from cabins.",
  },
  {
    type: "navi-carrier-strike-group", badge: "DEFENSE", badgeKind: "defense",
    logo: null,
    wmLine1: "U.S. NAVY",
    wmLine2: "CSG · FOUR",
    metric: "90 days", metricLabel: "rapid deployment",
    name: "Navy Carrier Strike Group Four",
    desc: "Dual VoIP SIP + NIST SP 800-171 across four TSVs.",
  },
  {
    type: "navy-communications", badge: "DEFENSE", badgeKind: "defense",
    logo: null,
    wmLine1: "U.S. NAVY",
    wmLine2: "NAS KEY WEST",
    metric: "60 days", metricLabel: "TDM → VoIP",
    name: "Navy Communications · NAS Key West",
    desc: "Leveraged CAT 3 cabling, bypassed legacy infrastructure.",
  },
  {
    type: "national-guard", badge: "DEFENSE", badgeKind: "defense",
    logo: null,
    wmLine1: "AIR NAT'L GUARD",
    wmLine2: "WYOMING",
    metric: "0 sec", metricLabel: "porting downtime",
    name: "Wyoming Air National Guard",
    desc: "LSTDM-to-SIP trunking. Kari's Law + E911. Avaya CM7 compatible.",
  },
  {
    type: "noaa-ship", badge: "GOVERNMENT", badgeKind: "defense",
    logo: null,
    wmLine1: "NOAA",
    wmLine2: "BELL M. SHIMADA",
    metric: "40 hrs", metricLabel: "remote training",
    name: "NOAA Ship Bell M. Shimada",
    desc: "PA/GA/PBX groom + onboard training at MOC-Pacific, Newport, OR.",
  },
  {
    type: "avaya", badge: "DEFENSE", badgeKind: "defense",
    logo: null,
    wmLine1: "AVAYA",
    wmLine2: "EDGE PARTNER",
    metric: "FedRAMP", metricLabel: "Gov Cloud",
    name: "Avaya · FedRAMP Government Cloud",
    desc: "NT Connect joins Avaya Edge Partner Program; FedRAMP-authorized cloud.",
  },
  {
    type: "nooz", badge: "MEDIA", badgeKind: "cruise",
    logo: require("../../assets/images/partners/logo-nooz.png"), logoTileDark: false,
    metric: "AI", metricLabel: "bias detection",
    name: "NOOZ.ai",
    desc: "AI-powered news bias detection (free iOS/Android).",
  },
];

const BADGE_COLORS = {
  cruise:  { bg: "rgba(37,112,189,0.16)", border: "rgba(37,112,189,0.5)" },
  defense: { bg: "rgba(241,178,80,0.14)", border: "rgba(241,178,80,0.5)" },
  hosp:    { bg: "rgba(76,183,130,0.14)", border: "rgba(76,183,130,0.5)" },
};

function ImpactStudiesView() {
  const { theme } = useTheme();
  const nav = useNavigation();
  const isMobile = useMobile();

  const accentFor = (kind) =>
    kind === "defense" ? theme.orange : kind === "hosp" ? theme.green : theme.brand2;

  return (
    <View style={[styles.outer, { paddingVertical: isMobile ? 80 : 120 }]}>
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>IMPACT STUDIES · ALL DEPLOYMENTS</Text>
            <Text style={[styles.title, { color: theme.text }]}>
              Eleven production deployments.{"\n"}One platform.
            </Text>
            <Text style={[styles.lede, { color: theme.textDim }]}>
              Cruise, defense, hospitality, healthcare, and media. Voxcraft ships across all of them.
              Click any card to read the full study.
            </Text>
          </View>
        </Reveal>

        <View style={[styles.grid, isMobile && styles.gridMobile]}>
          {STUDIES.map((s, i) => {
            const accent = accentFor(s.badgeKind);
            const badge = BADGE_COLORS[s.badgeKind];
            return (
              <View key={s.type} style={[styles.cell, isMobile && styles.cellMobile]}>
                <Reveal delay={(i % 3) + 1} style={styles.revealFill}>
                  <WebClass
                    className="voxcraft-ind-card"
                    style={[styles.card, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}
                  >
                    <Pressable
                      onPress={() => nav.navigate(routes.impactstudy, { type: s.type })}
                      style={styles.cardInner}
                    >
                      <View style={styles.topRow}>
                        <LogoTile theme={theme} entry={s} />
                        <View style={[styles.badge, { backgroundColor: badge.bg, borderColor: badge.border }]}>
                          <Text style={[styles.badgeText, { color: accent }]}>{s.badge}</Text>
                        </View>
                      </View>

                      <View style={styles.metricRow}>
                        <Text style={[styles.metric, { color: accent }]}>{s.metric}</Text>
                        <Text style={[styles.metricLabel, { color: theme.textDim }]}>{s.metricLabel}</Text>
                      </View>

                      <Text style={[styles.name, { color: theme.text }]}>{s.name}</Text>
                      <Text style={[styles.desc, { color: theme.textDim }]}>{s.desc}</Text>

                      <View style={[styles.linkWrap, { borderTopColor: theme.borderSoft }]}>
                        <Text style={[styles.link, { color: accent }]}>Read the impact study →</Text>
                      </View>
                    </Pressable>
                  </WebClass>
                </Reveal>
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
}

function LogoTile({ theme, entry }) {
  const tileBg = entry.logoTileDark ? theme.bg2 : "#f1f2f4";
  const tileBorder = entry.logoTileDark ? theme.borderSoft : "transparent";
  return (
    <View style={[styles.logoTile, { backgroundColor: tileBg, borderColor: tileBorder, borderWidth: entry.logoTileDark ? 1 : 0 }]}>
      {entry.logo ? (
        <Image source={entry.logo} style={styles.logoImg} resizeMode="contain" />
      ) : (
        <View style={styles.wmStack}>
          <Text style={styles.wmLine1}>{entry.wmLine1}</Text>
          <Text style={styles.wmLine2}>{entry.wmLine2}</Text>
        </View>
      )}
    </View>
  );
}

class ImpactStudiesController extends BaseMarketingController {
  componentDidMount() {
    super.componentDidMount && super.componentDidMount();
    if (typeof document !== "undefined") {
      document.title = "Impact Studies | Voxcraft";
    }
  }

  render() {
    return (
      <NtMarketingPageContainer>
        <ImpactStudiesView />
      </NtMarketingPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  outer: { paddingHorizontal: 28 },
  container: { maxWidth: 1280, marginHorizontal: "auto", width: "100%" },
  head: { maxWidth: 760, marginBottom: 56 },
  eyebrow: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 12, textTransform: "uppercase" },
  title: { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1, lineHeight: 46, marginBottom: 16 },
  lede: { fontSize: 17, lineHeight: 27 },

  grid: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", marginHorizontal: -9 },
  gridMobile: { flexDirection: "column", marginHorizontal: 0 },
  cell: { width: "33.33%", padding: 9 },
  cellMobile: { width: "100%", padding: 0, paddingBottom: 12 },
  revealFill: { flex: 1, width: "100%" },

  card: { flex: 1, borderRadius: 14, borderWidth: 1, overflow: "hidden" },
  cardInner: { flex: 1, padding: 26 },

  topRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 22 },

  logoTile: {
    height: 52, minWidth: 110,
    paddingHorizontal: 12, paddingVertical: 8,
    borderRadius: 10,
    alignItems: "center", justifyContent: "center",
  },
  logoImg: { width: 90, height: 36 },
  wmStack: { alignItems: "center" },
  wmLine1: { fontFamily: FONT_DISPLAY, fontWeight: "800", fontSize: 14, letterSpacing: 2, color: "#131417" },
  wmLine2: { fontFamily: FONT_MONO, fontWeight: "500", fontSize: 8.5, letterSpacing: 1.4, color: "#555", marginTop: 2 },

  badge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, borderWidth: 1 },
  badgeText: { fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: "700", letterSpacing: 1, textTransform: "uppercase" },

  metricRow: { flexDirection: "row", alignItems: "baseline", marginBottom: 12, gap: 8 },
  metric: { fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: "800", letterSpacing: -1.2, lineHeight: 34 },
  metricLabel: { fontSize: 12.5, lineHeight: 17, flex: 1 },

  name: { fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: "700", letterSpacing: -0.2, marginBottom: 6 },
  desc: { fontSize: 13, lineHeight: 20, marginBottom: 16, flex: 1 },

  linkWrap: { borderTopWidth: 1, paddingTop: 12 },
  link: { fontSize: 12.5, fontWeight: "600" },
});

export default ImpactStudiesController;
